import { Head, router } from "@inertiajs/react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { AnalyticsErrorState } from "@/components/brand/analytics/analytics-error-state"
import { BrandAnalyticsTabs } from "@/components/brand/analytics/brand-analytics-tabs"
import { ChannelAnalyticsFilterBar } from "@/components/brand/analytics/channel/channel-analytics-filter-bar"
import { ChannelGrowthChart } from "@/components/brand/analytics/channel/channel-growth-chart"
import { ChannelKpiStrip } from "@/components/brand/analytics/channel/channel-kpi-strip"
import { ChannelNotConnectedCard } from "@/components/brand/analytics/channel/channel-not-connected-card"
import { ChannelPopulatedSkeleton } from "@/components/brand/analytics/channel/channel-skeletons"
import { ChannelSyncingCard } from "@/components/brand/analytics/channel/channel-syncing-card"
import { ChannelVideosTable } from "@/components/brand/analytics/channel/channel-videos-table"
import { TiktokReconnectAlert } from "@/components/social-accounts/tiktok-reconnect-alert"
import AppLayout from "@/layouts/app-layout"
import { CHANNEL_RELOAD_ONLY } from "@/types/channel-analytics"
import type { ChannelAnalyticsProps } from "@/types/channel-analytics"

/**
 * OKS-734 — Brand Channel analytics dashboard (`/brand/analytics/channel`).
 *
 * Fourth tab on the brand analytics surface (Missions · Crew · EMV ·
 * Channel). The brand's own TikTok channel growth + video performance over
 * a selectable window (7/30/90d).
 *
 * # State branch (TRD KD-8 / UX Spec §3)
 *
 *   1. `state === "error"`         → `<AnalyticsErrorState>` (Retry).
 *   2. `state === "not_connected"` → `<ChannelNotConnectedCard>` (replaces
 *      the whole content area — no chart shell).
 *   3. `state === "syncing"`       → `<ChannelSyncingCard>`.
 *   4. `state === "populated"`     → filter bar + KPI strip + growth chart
 *      + video table. On window/metric/sort partial reload the body swaps
 *      to skeletons (client-side loading — NOT a server state).
 *
 * In every non-not_connected, non-error state an expired/errored
 * connection renders an amber reconnect banner ATOP the body (KD-7) while
 * the historical data still renders.
 *
 * # Partial reload
 *
 * Filter/sort/pagination route via `router.get` with
 * `only: CHANNEL_RELOAD_ONLY`. The shell + tab strip + header stay mounted.
 */
export default function BrandAnalyticsChannelIndex({
  state,
  window,
  metric,
  connection,
  reconnect_url: reconnectUrl,
  channel,
  kpis,
  videos,
}: ChannelAnalyticsProps) {
  const { t } = useTranslation()
  const loading = useChannelReloading()

  const showReconnectBanner =
    state !== "not_connected" &&
    state !== "error" &&
    connection != null &&
    connection.status !== "connected"

  return (
    <AppLayout>
      <Head title={t("channel.head_title")} />
      <div
        className="flex flex-col gap-6 p-4 md:p-6"
        data-testid="channel-page"
      >
        <BrandAnalyticsTabs active="channel" />

        <header className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("channel.heading")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("channel.subheading")}
          </p>
        </header>

        {showReconnectBanner ? (
          <TiktokReconnectAlert reconnect_url={reconnectUrl} />
        ) : null}

        {state === "error" ? (
          <AnalyticsErrorState reloadOnly={[...CHANNEL_RELOAD_ONLY]} />
        ) : state === "not_connected" ? (
          <ChannelNotConnectedCard reconnectUrl={reconnectUrl} />
        ) : state === "syncing" ? (
          <ChannelSyncingCard />
        ) : (
          <>
            <ChannelAnalyticsFilterBar
              window={window}
              metric={metric}
              sort={videos?.sort ?? "views"}
              dir={videos?.dir ?? "desc"}
            />
            {loading ? (
              <ChannelPopulatedSkeleton />
            ) : (
              <>
                {kpis ? <ChannelKpiStrip kpis={kpis} /> : null}
                {channel ? <ChannelGrowthChart channel={channel} /> : null}
                {videos ? (
                  <ChannelVideosTable
                    videos={videos}
                    window={window}
                    metric={metric}
                  />
                ) : null}
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

/**
 * Tracks the in-flight partial reload so the populated body can swap to
 * skeletons (UX Spec §3 — no spinner, no layout shift). Scoped to partial
 * reloads (`visit.only.length > 0`), which on this page are only the
 * filter/sort/pagination visits.
 */
function useChannelReloading(): boolean {
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const isPartial = (event: { detail: { visit: { only: string[] } } }) =>
      (event.detail.visit.only?.length ?? 0) > 0

    const stopStart = router.on("start", (event) => {
      if (isPartial(event)) setLoading(true)
    })
    const stopFinish = router.on("finish", (event) => {
      if (isPartial(event)) setLoading(false)
    })

    return () => {
      stopStart()
      stopFinish()
    }
  }, [])

  return loading
}
